import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { equipmentService } from '../services/dataService';
import { formatDateTime, StatusPill, toLabel } from './DetailDashboard';
import styles from '../pages/assets/css/DetailDashboard.module.css';

const ONLINE_TONE = {
  online: 'active',
  idle: 'pending',
  offline: 'failed',
  unknown: 'unknown',
};

const emptyOverride = { ae_title: '', host: '', port: '' };

/**
 * Online status, ping (C-ECHO), current AE link with unlink, and IP history for one machine.
 */
const EquipmentConnectionPanel = ({ equipmentId, connection, canWrite, onRefresh }) => {
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const [override, setOverride] = useState(emptyOverride);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [unlinkReason, setUnlinkReason] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const link = connection.active_link;
  const target = connection.echo_target;
  const lastCheck = pingResult?.check || connection.last_check;

  const handlePing = async () => {
    setPinging(true);
    setError('');
    setMessage('');
    try {
      const result = await equipmentService.echo(equipmentId, showOverride ? override : {});
      setPingResult(result);
      await onRefresh();
    } catch (err) {
      setError(err?.detail || 'Ping failed to run');
    } finally {
      setPinging(false);
    }
  };

  const handleUnlink = async () => {
    if (!link) return;
    setUnlinking(true);
    setError('');
    setMessage('');
    try {
      const result = await equipmentService.unlinkPingRequest(link.id, unlinkReason.trim());
      setConfirmUnlink(false);
      setUnlinkReason('');
      setMessage(
        `Unlinked ${result.link.ae_title}. Its next ping will appear in the approval queue, `
        + 'where you can link this equipment to the correct machine.',
      );
      if (result.orthanc_warning) setError(`Unlinked, but Orthanc reported: ${result.orthanc_warning}`);
      await onRefresh();
    } catch (err) {
      setError(err?.detail || 'Failed to unlink');
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>Connection &amp; Online Status</h2>
        <div className={styles.buttonRow}>
          <Link className={styles.secondaryButton} to={`/received-studies?equipment_id=${equipmentId}`}>Received studies</Link>
          <button type="button" className={styles.secondaryButton} onClick={onRefresh}>Refresh</button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handlePing}
            disabled={pinging || (!target.host && !(showOverride && override.host))}
          >
            {pinging ? 'Pinging…' : 'Ping machine'}
          </button>
        </div>
      </div>

      <div className={styles.statusLine}>
        <StatusPill value={ONLINE_TONE[connection.online_status]} label={toLabel(connection.online_status)} />
        <span>{connection.online_reason}</span>
        <span className={styles.muted}>Last DICOM activity: {formatDateTime(connection.last_seen_at)}</span>
      </div>

      {message ? <div className={styles.success}>{message}</div> : null}
      {error ? <div className={styles.error}>{error}</div> : null}
      {connection.ip_conflict ? (
        <div className={styles.warning}>
          AE title <strong>{link?.ae_title}</strong> was seen from several IP addresses in the last few minutes
          ({connection.conflict_ips.join(', ')}). Two machines may be configured with the same AE title.
        </div>
      ) : null}

      <div className={styles.chartGrid}>
        <div>
          <h3 className={styles.miniHeading}>Linked machine</h3>
          {link ? (
            <>
              <dl className={styles.kvList}>
                <div><dt>AE title</dt><dd><code>{link.ae_title}</code></dd></div>
                <div><dt>Current IP</dt><dd>{link.ip_addr}</dd></div>
                <div><dt>DICOM port</dt><dd>{link.port}</dd></div>
                <div><dt>Linked since</dt><dd>{formatDateTime(link.approved_at)}</dd></div>
                <div><dt>Discovered via</dt><dd>{toLabel(link.request_type)}</dd></div>
              </dl>
              {canWrite && !confirmUnlink ? (
                <button type="button" className={styles.dangerButton} onClick={() => setConfirmUnlink(true)}>
                  Unlink machine
                </button>
              ) : null}
              {confirmUnlink ? (
                <div className={styles.confirmBox}>
                  <p>
                    Unlink <strong>{link.ae_title}</strong>? It stops receiving worklists until a ping is linked
                    to this equipment again.
                  </p>
                  <input
                    className={styles.filterInput}
                    placeholder="Reason (optional), e.g. machine replaced"
                    value={unlinkReason}
                    onChange={(e) => setUnlinkReason(e.target.value)}
                  />
                  <div className={styles.buttonRow}>
                    <button type="button" className={styles.dangerButton} onClick={handleUnlink} disabled={unlinking}>
                      {unlinking ? 'Unlinking…' : 'Confirm unlink'}
                    </button>
                    <button type="button" className={styles.secondaryButton} onClick={() => setConfirmUnlink(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className={styles.muted}>
              Not linked. Approve this machine&apos;s ping in the Equipment page&apos;s ping request queue to link it.
            </p>
          )}
        </div>

        <div>
          <h3 className={styles.miniHeading}>Ping (DICOM C-ECHO)</h3>
          <p className={styles.muted}>
            {target.host
              ? <>Orthanc sends a C-ECHO to <code>{target.ae_title}@{target.host}:{target.port}</code> ({target.source}).</>
              : 'No address known yet: link a ping, set the equipment DICOM host, or test an address below.'}
          </p>
          <label className={styles.toggleLabel}>
            <input type="checkbox" checked={showOverride} onChange={(e) => setShowOverride(e.target.checked)} />
            Test a different address
          </label>
          {showOverride ? (
            <div className={styles.overrideGrid}>
              <input
                className={styles.filterInput}
                placeholder={`AE title (${target.ae_title || '—'})`}
                value={override.ae_title}
                onChange={(e) => setOverride((v) => ({ ...v, ae_title: e.target.value.toUpperCase() }))}
                maxLength={16}
              />
              <input
                className={styles.filterInput}
                placeholder={`Host (${target.host || 'IP address'})`}
                value={override.host}
                onChange={(e) => setOverride((v) => ({ ...v, host: e.target.value.trim() }))}
              />
              <input
                className={styles.filterInput}
                placeholder={`Port (${target.port})`}
                inputMode="numeric"
                value={override.port}
                onChange={(e) => setOverride((v) => ({ ...v, port: e.target.value.replace(/\D/g, '') }))}
              />
            </div>
          ) : null}
          {lastCheck ? (
            <div className={lastCheck.success ? styles.success : styles.error}>
              {lastCheck.success
                ? `Responded in ${lastCheck.latency_ms} ms`
                : `No response: ${lastCheck.error || 'unknown error'}`}
              <span className={styles.subText}>
                {lastCheck.ae_title}@{lastCheck.host}:{lastCheck.port} · {formatDateTime(lastCheck.checked_at)}
                {lastCheck.checked_by ? ` · ${lastCheck.checked_by}` : ''}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <h3 className={styles.miniHeading}>IP address history</h3>
      {connection.ip_history.length ? (
        <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
          <table className={styles.table}>
            <thead>
              <tr><th>IP address</th><th>First seen</th><th>Last seen</th><th>Times seen</th><th>Via</th></tr>
            </thead>
            <tbody>
              {connection.ip_history.map((entry) => (
                <tr key={entry.ip_addr}>
                  <td><code>{entry.ip_addr}</code>{entry.ip_addr === link?.ip_addr ? <span className={styles.primaryTag}>Current</span> : null}</td>
                  <td>{formatDateTime(entry.first_seen_at)}</td>
                  <td>{formatDateTime(entry.last_seen_at)}</td>
                  <td>{entry.seen_count}</td>
                  <td>{toLabel(entry.last_request_type)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.muted}>No IP addresses recorded for the current link yet.</p>
      )}

      {connection.recent_checks.length ? (
        <>
          <h3 className={styles.miniHeading}>Recent pings</h3>
          <ul className={styles.contactList}>
            {connection.recent_checks.map((check) => (
              <li key={check.id}>
                <span>
                  <StatusPill value={check.success ? 'active' : 'failed'} label={check.success ? 'Reachable' : 'No response'} />
                  {' '}<code>{check.ae_title}@{check.host}:{check.port}</code>
                  {check.success ? ` · ${check.latency_ms} ms` : ''}
                </span>
                <span className={styles.muted}>
                  {formatDateTime(check.checked_at)}{check.checked_by ? ` · ${check.checked_by}` : ''}
                  {!check.success && check.error ? ` · ${check.error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {connection.previous_links.length ? (
        <>
          <h3 className={styles.miniHeading}>Previous links</h3>
          <ul className={styles.contactList}>
            {connection.previous_links.map((previous) => (
              <li key={previous.id}>
                <span>
                  <code>{previous.ae_title}</code> · {previous.ip_addr} <StatusPill value={previous.approval_status === 'unlinked' ? 'cancelled' : previous.approval_status} label={toLabel(previous.approval_status)} />
                </span>
                <span className={styles.muted}>
                  {previous.approval_reason || '—'} · updated {formatDateTime(previous.updated_at)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
};

export default EquipmentConnectionPanel;
